import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ChatContextData, ChatMessage, EnhancedAIResponse } from '@/components/chat/types';

interface CreationContext extends Record<string, unknown> {
  suggestedPlatforms?: string[];
  routedFromChat?: boolean;
  routingMode?: string;
}

interface ComposerContext extends Record<string, unknown> {
  mode?: string;
  source?: string;
  context?: CreationContext;
  brandVoice?: string;
  platforms?: string[];
}

interface ChatContextType {
  context: ChatContextData | null;
  setContext: (context: ChatContextData) => void;
  messages: ChatMessage[];
  askQuestion: (question: string) => Promise<EnhancedAIResponse>;
  isLoading: boolean;
  clearMessages: () => void;
  addMessage: (message: ChatMessage) => void;
  currentContext: ChatContextData | null;

  routeToCreationMode: (
    mode: 'generic' | 'product-based' | 'competitive' | 'enhancement' | 'ideas',
    context?: CreationContext,
  ) => void;
  openComposerWithContext: (context: ComposerContext) => void;
  switchToConversationMode: () => void;
  currentMode: 'conversation' | 'creation' | 'discovery';
  setCurrentMode: (mode: 'conversation' | 'creation' | 'discovery') => void;

  unifiedAI: null;

  navigateToComposerWithContext: (
    contentData: Record<string, unknown>,
    chatContext?: Record<string, unknown>,
  ) => void;
  navigateToAnalyticsWithContext: (performanceQuery: Record<string, unknown>) => void;
  hasIncomingContext: boolean;
  incomingContext: null;
}

const ChatContext = createContext<ChatContextType | null>(null);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

const UNAVAILABLE_RESPONSE: EnhancedAIResponse = {
  answer: 'The conversational AI assistant is temporarily unavailable.',
  responseType: 'general',
  metadata: {
    confidenceScore: 0,
    timestamp: new Date(0).toISOString(),
    processingTimeMs: 0,
    servicesUsed: [],
    aiDecisionsMade: 0,
  },
};

// ChatProvider mounts ABOVE the Router (see App.tsx), so we cannot use the
// React Router hooks here. The navigation handlers below use direct
// window.location.assign() but ONLY accept internal (same-origin) paths.
function navigateInternal(to: string): void {
  if (typeof window === 'undefined') return;
  if (!to.startsWith('/') || to.startsWith('//')) return;
  window.location.assign(to);
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [context, setContext] = useState<ChatContextData | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMode, setCurrentMode] = useState<'conversation' | 'creation' | 'discovery'>(
    'discovery',
  );

  const askQuestion = useCallback(async (): Promise<EnhancedAIResponse> => {
    return UNAVAILABLE_RESPONSE;
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const openComposerWithContext = useCallback((composeContext: ComposerContext) => {
    sessionStorage.setItem('aiGeneratedContent', JSON.stringify(composeContext));
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      navigateInternal('/?mode=image');
    }
  }, []);

  const routeToCreationMode = useCallback(
    (
      mode: 'generic' | 'product-based' | 'competitive' | 'enhancement' | 'ideas',
      creationContext?: CreationContext,
    ) => {
      setCurrentMode('creation');
      openComposerWithContext({
        mode,
        source: 'chat',
        context: creationContext,
        platforms: creationContext?.suggestedPlatforms || [],
        ...creationContext,
      });
    },
    [openComposerWithContext],
  );

  const switchToConversationMode = useCallback(() => {
    setCurrentMode('conversation');
  }, []);

  const navigateToComposerWithContext = useCallback(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      navigateInternal('/?mode=image');
    }
  }, []);

  const navigateToAnalyticsWithContext = useCallback(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== '/insights') {
      navigateInternal('/insights');
    }
  }, []);

  const value = useMemo<ChatContextType>(
    () => ({
      context,
      setContext,
      messages,
      askQuestion,
      isLoading: false,
      clearMessages,
      addMessage,
      currentContext: context,
      routeToCreationMode,
      openComposerWithContext,
      switchToConversationMode,
      currentMode,
      setCurrentMode,
      unifiedAI: null,
      navigateToComposerWithContext,
      navigateToAnalyticsWithContext,
      hasIncomingContext: false,
      incomingContext: null,
    }),
    [
      context,
      messages,
      askQuestion,
      clearMessages,
      addMessage,
      routeToCreationMode,
      openComposerWithContext,
      switchToConversationMode,
      currentMode,
      navigateToComposerWithContext,
      navigateToAnalyticsWithContext,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export { ChatContext };
